<script setup lang="ts">
import { CourseContentActivityType, ComparisonId, CourseSubViewType } from "~/enums/course.enums";

const {
  course,
  preliminaryCourse,
  hasPreliminaryCourse,
  associatedWorkshop,
  hasAssociatedWorkshop,
  updatedVersion,
  hasUpdatedVersion,
  showFeedbackForm,
  feedbackFormSkippable,
  displayClaimCertificateForm,
  progress,
  subView,
  dismissCertificateForm,
  getLesson,
  getActivity,
  showActivityInformationModal,
  isLoading,
  setCourseId,
  updateSubView,
  refreshCourse,
  onCourseLoaded,
} = useCourseState();

const { virtualPageView } = useTracking();

virtualPageView('course', 'course-details');

const route = useRoute();

const currentActivityId = computed<string | undefined>(() => {
  if (route.params.lessonId) {
    return route.params.lessonId as string;
  }
  if (route.params.quizId) {
    return route.params.quizId as string;
  }
  if (route.params.caseId) {
    return route.params.caseId as string;
  }

  if (route.name === CourseContentActivityType.Comparison) {
    return ComparisonId.toString();
  }

  return undefined;
});

const isLocked = computed(() => {
  if (subView.value === CourseSubViewType.Lesson) {
    const activity = getLesson(route.params.lessonId as string);
    return activity?.locked ?? true;
  }

  if (subView.value === CourseSubViewType.Quiz) {
    const activity = getActivity(route.params.quizId as string);
    return activity?.locked ?? true;
  }

  return false;
});

const showSidebar = computed(() => {
  return course.value && [CourseSubViewType.Lesson, CourseSubViewType.Quiz, CourseSubViewType.Comparison].includes(subView.value as CourseSubViewType);
});

const hideSidebarOnMobile = computed(() => {
  return isLocked.value || [
    CourseSubViewType.Quiz,
    CourseSubViewType.Comparison,
  ].includes(subView.value as CourseSubViewType);
});

const showPage = computed(() => {
  return [
    CourseSubViewType.Lesson,
    CourseSubViewType.Quiz,
    CourseSubViewType.Case,
    CourseSubViewType.Comparison,
  ].includes(subView.value as CourseSubViewType);
});

const showToolbar = computed(() => {
  return [
    CourseSubViewType.Lesson,
    CourseSubViewType.Quiz,
    CourseSubViewType.Comparison,
  ].includes(subView.value as CourseSubViewType);
});

const isScrollableArea = computed(() => {
  const isNotCourseOrLesson = ![
    CourseSubViewType.Course,
    CourseSubViewType.Lesson,
  ].includes(subView.value as CourseSubViewType);

  const isLockedLesson = (subView.value === CourseSubViewType.Lesson && isLocked.value);

  return isNotCourseOrLesson || isLockedLesson;
});

const showUpsellCard = computed(() => {
  return isLocked.value && [
    CourseSubViewType.Quiz,
    CourseSubViewType.Lesson,
  ].includes(subView.value as CourseSubViewType);
});

onBeforeMount(() => {
  updateSubView();
  setCourseId(route.params.courseId as string);
});

onCourseLoaded(() => {
  isLoading.value = false;

  if (!course.value) {
    return;
  }

  useHead({
    title: `${course.value?.courseTitle || 'Course'}`,
  });
});

watch(
  () => route.fullPath,
  () => {
    refreshCourse();
    updateSubView();
  },
);

definePageMeta({
  name: 'course',
  middleware: 'course-middleware',
});

useHead({
  title: 'Course',
});
</script>

<template>
  <div
    :class="[
      'page-course overflow-x-hidden',
      'lg:grid grid-cols-[1fr_auto] flex flex-col',
      {'h-[100svh] overflow-y-hidden': subView !== CourseSubViewType.Course}
    ]"
  >
    <main
      class="w-full flex flex-col"
      :class="[
        {'h-full overflow-y-scroll': isScrollableArea},
        {'h-full': isLoading}
      ]"
    >
      <CourseToolbar
        v-if="showToolbar"
        :current-activity-id="currentActivityId as string"
      />

      <div
        v-if="isLoading"
        class="flex items-center justify-center"
        :class="subView === CourseSubViewType.Course ? 'h-[calc(100svh-86px)]' : 'h-full'"
      >
        <Loader />
      </div>

      <template v-else-if="course">
        <UpsellCard v-if="showUpsellCard" />

        <NuxtPage v-else-if="showPage" />

        <div v-if="subView === CourseSubViewType.Course">
          <div
            v-if="hasUpdatedVersion && updatedVersion"
            class="bg-neutral-10"
          >
            <CourseRelatedContent
              :course="updatedVersion"
              :updated-version="true"
              :sunset-at="course.sunsetAt"
            />
          </div>

          <CourseDetails @show-activity-information="showActivityInformationModal = true" />

          <div class="bg-neutral-10">
            <CourseRelatedContent
              v-if="hasPreliminaryCourse && preliminaryCourse"
              :course="preliminaryCourse"
            />
            <CourseRelatedContent
              v-else-if="hasAssociatedWorkshop && associatedWorkshop"
              :course="associatedWorkshop"
            />
          </div>
        </div>
      </template>
    </main>

    <CourseSidebar
      v-if="showSidebar"
      :class="{'lg:block hidden': hideSidebarOnMobile}"
      :current-activity-id="currentActivityId"
    />

    <CourseActivityInformation
      v-if="course && showActivityInformationModal && course.cmeCredits > 0"
      v-model="showActivityInformationModal"
      :course="course"
      :activity-information-accepted="course.activityInformationAccepted"
    />
    <CourseFeedbackModal
      v-if="course && subView !== CourseSubViewType.Course"
      v-model="showFeedbackForm"
      :course-id="course.id"
      :course-title="course.courseTitle"
      :course-type="course.type"
      :skippable="feedbackFormSkippable"
      :progress="progress"
    />
    <CourseClaimCertificateDialog
      v-if="course"
      :visible="displayClaimCertificateForm"
      @dismiss="dismissCertificateForm"
      @downloaded="dismissCertificateForm"
    />
  </div>
</template>

<style lang="scss">
.page-course {
  .sidebar {
    border-top: 1px solid var(--palette-neutral-20);

    @screen lg {
      border-left: 1px solid var(--palette-neutral-20);
      border-top: none;
    }
  }
}

body, #__nuxt {
  height: auto !important;
  min-height: auto !important;
}
</style>
